import {
  METADATA_HOSTS,
  type SettingDto,
  type SettingKey,
  type SuggestionLanguagesContextDto,
} from "@shelflist/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  SuggestionLanguagesField,
  type SuggestionLanguagesValue,
} from "@/components/admin/suggestion-languages-field";
import { Field, fieldA11y } from "@/components/field";
import { Page } from "@/components/page";
import { LoadingState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, call } from "@/lib/api";
import { useErrorMessage } from "@/lib/errors";
import { instanceSettingsQuery, metadataKeys } from "@/lib/metadata";

export const Route = createFileRoute("/_app/admin/settings")({
  component: InstanceSettingsPage,
});

const API_KEY: SettingKey = "metadata.googleBooks.apiKey";

type KeyAction = { kind: "keep" } | { kind: "replace"; value: string } | { kind: "remove" };

export function InstanceSettingsPage() {
  const { t } = useTranslation("instanceSettings");
  const { data, error } = useQuery(instanceSettingsQuery);
  return (
    <Page title={t("title")} description={t("description")} width="narrow">
      {error ? (
        <p role="alert" className="text-destructive">
          {t("loadError")}
        </p>
      ) : data ? (
        <SettingsForm settings={data.settings} suggestionLanguages={data.suggestionLanguages} />
      ) : (
        <LoadingState />
      )}
    </Page>
  );
}

function Switch({
  id,
  label,
  help,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        className="accent-primary mt-1 size-4"
        checked={checked}
        aria-describedby={`${id}-help`}
        onChange={(event) => onChange(event.target.checked)}
      />
      <div className="grid gap-1">
        <Label htmlFor={id}>{label}</Label>
        <p id={`${id}-help`} className="text-muted-foreground text-sm">
          {help}
        </p>
      </div>
    </div>
  );
}

const LANGUAGES_KEY: SettingKey = "authorSuggestions.languages";
const INCLUDE_UNKNOWN_KEY: SettingKey = "authorSuggestions.includeUnknownLanguage";

function SettingsForm({
  settings,
  suggestionLanguages,
}: {
  settings: SettingDto[];
  suggestionLanguages: SuggestionLanguagesContextDto | undefined;
}) {
  const { t } = useTranslation("instanceSettings");
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const byKey = (key: SettingKey) => settings.find((setting) => setting.key === key);
  /** The boolean switches; list values (`authorSuggestions.languages`) are handled by their own section. */
  const flag = (key: SettingKey, fallback: boolean): boolean => {
    const value = byKey(key)?.value;
    return typeof value === "boolean" ? value : fallback;
  };
  const initial = {
    master: flag("metadata.lookupsEnabled", true),
    openLibrary: flag("metadata.openLibrary.enabled", true),
    googleBooks: flag("metadata.googleBooks.enabled", true),
    dnb: flag("metadata.dnb.enabled", true),
    authorSuggestions: flag("authorSuggestions.enabled", true),
  };
  const savedLanguages = byKey(LANGUAGES_KEY)?.value;
  const [form, setForm] = useState(initial);
  const [languages, setLanguages] = useState<SuggestionLanguagesValue>({
    languages: Array.isArray(savedLanguages) ? savedLanguages : null,
    includeUnknown: flag(INCLUDE_UNKNOWN_KEY, false),
  });
  const [languagesError, setLanguagesError] = useState<string | undefined>();
  const [keyAction, setKeyAction] = useState<KeyAction>({ kind: "keep" });
  const [fieldError, setFieldError] = useState<string | undefined>();
  const apiKey = byKey(API_KEY);

  const save = useMutation({
    mutationFn: () => {
      const json: Record<string, unknown> = {
        "metadata.lookupsEnabled": form.master,
        "metadata.openLibrary.enabled": form.openLibrary,
        "metadata.googleBooks.enabled": form.googleBooks,
        "metadata.dnb.enabled": form.dnb,
        "authorSuggestions.enabled": form.authorSuggestions,
        [LANGUAGES_KEY]: languages.languages,
        [INCLUDE_UNKNOWN_KEY]: languages.includeUnknown,
      };
      if (keyAction.kind === "replace") json[API_KEY] = keyAction.value;
      if (keyAction.kind === "remove") json[API_KEY] = null;
      return call(api.admin.settings.$patch({ json: json as never }));
    },
    meta: { handlesErrors: true },
    onSuccess: (saved) => {
      queryClient.setQueryData(metadataKeys.settings, saved);
      void queryClient.invalidateQueries({ queryKey: metadataKeys.features });
      setKeyAction({ kind: "keep" });
      setFieldError(undefined);
      setLanguagesError(undefined);
      toast.success(t("saved"));
      for (const warning of saved.warnings) {
        toast.warning(
          warning.code === "google_api_key_quota_exceeded"
            ? t("metadata.warnings.quota")
            : warning.reason === "lookups_disabled"
              ? t("metadata.warnings.lookupsDisabled")
              : t("metadata.warnings.unreachable"),
          { duration: 12_000 },
        );
      }
    },
    onError: (error) => {
      const fields = (error as { fields?: Record<string, string> }).fields ?? {};
      setFieldError(fields[API_KEY]);
      setLanguagesError(fields[LANGUAGES_KEY]);
      toast.error(errorMessage(error));
    },
  });

  return (
    <form
      className="grid gap-6"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <section className="grid gap-4 rounded-lg border p-5" aria-labelledby="metadata-heading">
        <div className="grid gap-1">
          <h2 id="metadata-heading" className="text-lg font-semibold">
            {t("metadata.title")}
          </h2>
          <p className="text-muted-foreground text-sm">{t("metadata.description")}</p>
        </div>
        <Switch
          id="setting-master"
          label={t("metadata.master")}
          help={t("metadata.masterHelp")}
          checked={form.master}
          onChange={(master) => setForm((f) => ({ ...f, master }))}
        />
        <div className="grid gap-1 text-sm">
          <h3 className="font-medium">{t("metadata.hostsTitle")}</h3>
          <p className="text-muted-foreground">{t("metadata.hostsHelp")}</p>
          <ul className="text-muted-foreground list-disc pl-5 font-mono text-xs">
            {METADATA_HOSTS.map((host) => (
              <li key={host}>{host === "archive.org" ? "*.archive.org" : host}</li>
            ))}
          </ul>
        </div>
        <fieldset className="grid gap-4 border-t pt-4" disabled={!form.master}>
          <Switch
            id="setting-open-library"
            label={t("metadata.openLibrary")}
            help={t("metadata.openLibraryHelp")}
            checked={form.openLibrary}
            onChange={(openLibrary) => setForm((f) => ({ ...f, openLibrary }))}
          />
          <Switch
            id="setting-google-books"
            label={t("metadata.googleBooks")}
            help={t("metadata.googleBooksHelp")}
            checked={form.googleBooks}
            onChange={(googleBooks) => setForm((f) => ({ ...f, googleBooks }))}
          />
          <Switch
            id="setting-dnb"
            label={t("metadata.dnb")}
            help={t("metadata.dnbHelp")}
            checked={form.dnb}
            onChange={(dnb) => setForm((f) => ({ ...f, dnb }))}
          />
          <ApiKeyField
            configured={apiKey?.configured === true}
            last4={apiKey?.last4 ?? null}
            action={keyAction}
            error={fieldError}
            onChange={setKeyAction}
          />
        </fieldset>
      </section>
      <section
        className="grid gap-4 rounded-lg border p-5"
        aria-labelledby="author-suggestions-heading"
      >
        <div className="grid gap-1">
          <h2 id="author-suggestions-heading" className="text-lg font-semibold">
            {t("authorSuggestions.title")}
          </h2>
          <p className="text-muted-foreground text-sm">{t("authorSuggestions.description")}</p>
        </div>
        <Switch
          id="setting-author-suggestions"
          label={t("authorSuggestions.switch")}
          help={t("authorSuggestions.switchHelp")}
          checked={form.authorSuggestions}
          onChange={(authorSuggestions) => setForm((f) => ({ ...f, authorSuggestions }))}
        />
        {!form.master ? (
          <p className="text-muted-foreground text-sm">{t("authorSuggestions.needsLookups")}</p>
        ) : null}
        <Link
          to="/admin/author-mapping"
          className="w-fit text-sm underline-offset-4 hover:underline"
        >
          {t("authorSuggestions.mappingLink")}
        </Link>
        <SuggestionLanguagesField
          value={languages}
          context={suggestionLanguages}
          error={languagesError}
          onChange={(next) => {
            setLanguages(next);
            setLanguagesError(undefined);
          }}
        />
      </section>
      <div>
        <Button
          type="submit"
          disabled={
            save.isPending || (languages.languages !== null && languages.languages.length === 0)
          }
        >
          {save.isPending ? t("saving") : t("save")}
        </Button>
      </div>
    </form>
  );
}

function ApiKeyField({
  configured,
  last4,
  action,
  error,
  onChange,
}: {
  configured: boolean;
  last4: string | null;
  action: KeyAction;
  error?: string;
  onChange: (action: KeyAction) => void;
}) {
  const { t } = useTranslation("instanceSettings");
  const status =
    action.kind === "remove"
      ? t("metadata.apiKeyRemoved")
      : configured
        ? last4
          ? t("metadata.apiKeyConfigured", { last4 })
          : t("metadata.apiKeySet")
        : t("metadata.apiKeyNone");
  return (
    <div className="grid gap-2">
      <div className="text-sm font-medium">{t("metadata.apiKey")}</div>
      {action.kind === "replace" ? (
        <Field id="setting-api-key" label={t("metadata.apiKeyNew")} error={error}>
          <div className="flex gap-2">
            <Input
              {...fieldA11y("setting-api-key", error)}
              type="password"
              autoComplete="off"
              spellCheck={false}
              maxLength={200}
              value={action.value}
              onChange={(event) => onChange({ kind: "replace", value: event.target.value })}
            />
            <Button type="button" variant="outline" onClick={() => onChange({ kind: "keep" })}>
              {t("metadata.apiKeyCancel")}
            </Button>
          </div>
        </Field>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-sm" data-testid="api-key-status">
            {status}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange({ kind: "replace", value: "" })}
          >
            {t("metadata.apiKeyReplace")}
          </Button>
          {action.kind === "remove" ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ kind: "keep" })}
            >
              {t("metadata.apiKeyUndo")}
            </Button>
          ) : configured ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ kind: "remove" })}
            >
              {t("metadata.apiKeyRemove")}
            </Button>
          ) : null}
        </div>
      )}
      <p className="text-muted-foreground text-sm">{t("metadata.apiKeyHelp")}</p>
    </div>
  );
}
